import { useEffect, useState } from 'react';
import { CheckboxGroup, type CheckboxButtonOption, type CheckboxGroupProps } from 'framework/form';
import useOfficeService, { ANYWHERE_OFFICE_NAME, OFFICE_NAMES, type OfficeType } from '../officeService';

type OfficeCheckboxGroupProps = Omit<CheckboxGroupProps, 'options'> & {
  excludeAnywhere?: boolean;
  excludedOfficeIds?: number[];
};

const OfficeCheckboxGroup = ({
  excludeAnywhere = false,
  excludedOfficeIds = [],
  ...props
}: OfficeCheckboxGroupProps) => {
  const { findAll } = useOfficeService();
  const [options, setOptions] = useState<CheckboxButtonOption[]>([]);
  const excludedOfficeIdsKey = excludedOfficeIds.join(',');

  useEffect(() => {
    let isActive = true;
    const excludedIds = new Set(excludedOfficeIdsKey.split(',').filter(Boolean).map(Number));

    findAll()
      .then(offices => {
        if (!isActive) return;

        setOptions(
          offices
            .filter(
              (office: OfficeType) =>
                (!excludeAnywhere || office.name !== ANYWHERE_OFFICE_NAME) && !excludedIds.has(office.id),
            )
            .map((office: OfficeType) => ({
              value: office.id,
              label: OFFICE_NAMES[office.name] ?? office.name,
            })),
        );
      })
      .catch(() => {
        if (isActive) setOptions([]);
      });

    return () => {
      isActive = false;
    };
    // Office service is recreated with its API hook. The reference change is not a reload signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeAnywhere, excludedOfficeIdsKey]);

  return <CheckboxGroup {...props} options={options} />;
};

export default OfficeCheckboxGroup;
